/**
 * UserMenu Component
 * Following DDD principles - UI layer component
 *
 * Displays user information with dropdown menu
 * Refactored with custom hook using useMemo for performance optimization
 */

import React from 'react';
import classNames from 'classnames';
import { IconChevronDown } from '../Icons/IconChevron';
import { UserMenuProps } from './UserMenu.types';
import { useUserMenu } from './useUserMenu';
import './UserMenu.scss';

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  items,
  className,
}) => {
  const {
    isOpen,
    menuRef,
    displayName,
    userImage,
    avatarInitial,
    menuItems,
    toggleMenu,
    handleMenuItemClick,
  } = useUserMenu({
    user,
    items,
  });

  return (
    <div
      ref={menuRef}
      className={classNames('user-menu', className, {
        'user-menu--open': isOpen,
      })}
    >
      {/* User Info Row */}
      <div className='user-menu__trigger' onClick={toggleMenu}>
        {/* User Email/Username */}
        <span className='user-menu__name'>{displayName}</span>

        {/* User Avatar */}
        <div className='user-menu__avatar'>
          {userImage ? (
            <img
              src={userImage}
              alt={displayName}
              className='user-menu__avatar-image'
            />
          ) : (
            <div className='user-menu__avatar-placeholder'>
              {avatarInitial}
            </div>
          )}
        </div>

        {/* Chevron Icon */}
        <div
          className={classNames('user-menu__chevron', {
            'user-menu__chevron--open': isOpen,
          })}
        >
          <IconChevronDown />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className='user-menu__dropdown'>
          <div className='user-menu__dropdown-content'>
            {/* Render menu items dynamically */}
            {menuItems.map((item) => (
              <React.Fragment key={item.id}>
                {/* Show divider before item if specified */}
                {item.dividerBefore && <div className='user-menu__divider' />}
                
                <button
                  className={classNames('user-menu__item', {
                    'user-menu__item--danger': item.danger,
                  })}
                  onClick={() => handleMenuItemClick(item)}
                >
                  {item.icon && (
                    <span className='user-menu__item-icon'>{item.icon}</span>
                  )}
                  <span className='user-menu__item-label'>{item.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
